import React, { useState } from 'react';
import classNames from 'classnames/bind';
import PropTypes from 'prop-types';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

import Heading from 'components/shared/heading';
import Button from 'components/shared/button';

import useVideo from 'hooks/use-video';

import VolumeControl from 'icons/volume-control.inline.svg';
import PlayIcon from 'icons/play.inline.svg';
import QuoteIcon from 'icons/quote.inline.svg';

import shape1Primary from './images/shape-1-primary.svg';
import shape2Primary from './images/shape-2-primary.svg';
import shape1Secondary from './images/shape-1-secondary.svg';

import styles from './title-and-video-html.module.scss';

const cx = classNames.bind(styles);

const quoteAnimationVariants = {
    initial: {
        y: 0,
        opacity: 0,
        transition: { duration: 0.5, ease: [0.21, 0.49, 0.33, 1] },
    },
    appear: {
        y: '-50%',
        opacity: 1,
        transition: { duration: 0.5, ease: [0.21, 0.49, 0.33, 1] },
    },
};

const TitleAndVideoHtml = ({
    className: additionalClassName = '',
    title,
    description = null,
    quote = null,
    video,
    isVideoAutoplay = false,
    videobackgroundimage = null,
    videoBackgroundImageFluid = null,
    theme = 'primary',
}) => {

    const [sectionRef, inView] = useInView({ threshold: 1, triggerOnce: true });
    const [isVideoStartedPlaying, setIsVideoStartedPlaying] = useState(false);

    const {
        videoWrapperRef,
        videoRef,
        isUnmuteVisible,
        isVideoMuted,
        unmute,
        handleVideoEnd,
    } = useVideo();

    const handlePlayButtonClick = () => setIsVideoStartedPlaying(true);

    const isVisibleQuote = !isVideoStartedPlaying && quote && !isVideoAutoplay;

    return (
        <section className={cx('wrapper', theme, additionalClassName)} ref={sectionRef}>
            <div className="container">
                <div className={cx('head')}>
                    <Heading className={cx('title')} tag="h2" size="xl">
                        {title}
                    </Heading>
                    {description && <p className={cx('description')}>{description}</p>}
                </div>

                <div className={cx('video-wrapper')} ref={videoWrapperRef}>
                    <motion.div
                        className={cx('quote-wrapper')}
                        initial="initial"
                        variants={quoteAnimationVariants}
                        animate={inView && isVisibleQuote ? 'appear' : 'initial'}
                    >
                        <figure className={cx('quote')}>
                            <QuoteIcon className={cx('quote-icon')} />
                            <blockquote className={cx('quote-text')}>
                                <p>{quote}</p>
                            </blockquote>
                        </figure>
                    </motion.div>

                    <div className={cx('video-background')}>
                        {videobackgroundimage ? (
                            <img
                                src={videobackgroundimage.url}
                                alt={videobackgroundimage.alt}
                                height={440}
                                width={780}
                                loading="lazy"
                            />
                        ) : (
                            <GatsbyImage image={getImage(videoBackgroundImageFluid)} alt="" />
                        )}
                    </div>

                    {isVideoAutoplay && (
                        <>
                            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                            <video
                                className={cx('video')}
                                muted={isVideoMuted}
                                controls={!isVideoMuted}
                                playsInline
                                preload="none"
                                onEnded={handleVideoEnd}
                                ref={videoRef}
                            >
                                <source src={video} type="video/mp4" />
                            </video>
                            {isUnmuteVisible && (
                                <Button
                                    className={cx('button-mute')}
                                    theme="primary"
                                    size="sm"
                                    data-type="video-mute-button"
                                    onClick={unmute}
                                >
                                    <VolumeControl />
                                    enable audio
                                </Button>
                            )}
                        </>
                    )}

                    {!isVideoAutoplay && (
                        <>
                            {!isVideoStartedPlaying && (
                                <Button
                                    className={cx('play-button')}
                                    theme="tertiary"
                                    onClick={handlePlayButtonClick}
                                >
                                    <PlayIcon />
                                </Button>
                            )}

                            {isVideoStartedPlaying && (
                                // eslint-disable-next-line jsx-a11y/media-has-caption
                                <video className={cx('video')} autoPlay controls loop playsInline>
                                    <source src={video} type="video/mp4" />
                                </video>
                            )}
                        </>
                    )}

                    {theme === 'primary' ? (
                        <img
                            className={cx('shape-1', theme)}
                            src={shape1Secondary}
                            alt=""
                            loading="lazy"
                            aria-hidden
                        />
                    ) : (
                        <>
                            <img
                                className={cx('shape-1', theme)}
                                src={shape1Primary}
                                alt=""
                                loading="lazy"
                                aria-hidden
                            />
                            <img
                                className={cx('shape-2')}
                                src={shape2Primary}
                                alt=""
                                loading="lazy"
                                aria-hidden
                            />
                        </>
                    )}
                </div>
            </div>
        </section>
    );
};

TitleAndVideoHtml.propTypes = {
    className: PropTypes.string,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    quote: PropTypes.string,
    video: PropTypes.string.isRequired,
    isVideoAutoplay: PropTypes.bool,
    videobackgroundimage: PropTypes.shape({
        url: PropTypes.string,
        alt: PropTypes.string,
    }),
    videoBackgroundImageFluid: PropTypes.objectOf(PropTypes.any),
    theme: PropTypes.oneOf(['primary', 'secondary']),
};


export default TitleAndVideoHtml;
